import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

interface FixResults {
  total: number;
  updated: number;
  failed: number;
  errors: string[];
}

const FixCourseAssignments = () => {
  const [isFixing, setIsFixing] = useState(false);
  const [results, setResults] = useState<FixResults | null>(null);

  const handleFix = async () => {
    setIsFixing(true);
    setResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('fix-course-assignments', {
        method: 'POST'
      });

      if (error) {
        console.error('Error fixing assignments:', error);
        toast.error('Failed to fix course assignments');
        return;
      }

      if (data?.results) {
        setResults(data.results);
        
        if (data.results.updated > 0) {
          toast.success(`Successfully fixed ${data.results.updated} course assignments!`);
        } else if (data.results.total === 0) {
          toast.info('No course assignments need fixing');
        } else {
          toast.warning('Some assignments could not be fixed');
        }
      }
    } catch (error) {
      console.error('Exception fixing assignments:', error);
      toast.error('An error occurred while fixing assignments');
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fix Course Assignments Data</CardTitle>
        <CardDescription>
          Repairs course assignments with incorrect or missing total_modules values.
          This fixes certificate eligibility issues caused by corrupted assignment data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This tool will scan all course assignments and update those with total_modules = 0 or NULL
            to have the correct module count from the course_modules table.
          </AlertDescription>
        </Alert>

        <Button 
          onClick={handleFix} 
          disabled={isFixing}
          className="w-full"
        >
          {isFixing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Fixing Course Assignments...
            </>
          ) : (
            'Fix Course Assignments'
          )}
        </Button>

        {results && (
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{results.total}</div>
                    <div className="text-xs text-muted-foreground">Total Found</div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 flex items-center justify-center gap-1">
                      <CheckCircle className="h-5 w-5" />
                      {results.updated}
                    </div>
                    <div className="text-xs text-muted-foreground">Fixed</div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 flex items-center justify-center gap-1">
                      <XCircle className="h-5 w-5" />
                      {results.failed}
                    </div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {results.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="font-semibold mb-2">Errors encountered:</div>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    {results.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {results.errors.length > 5 && (
                      <li>...and {results.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FixCourseAssignments;
